import type { Page } from 'types/page';
import { Blocks as BlocksType } from 'types/blocks';

import type { PartnersReviewsResult } from 'lib/queries/reviews';
import type { WysiwygContentProps } from './wysiwyg-content';

import React from 'react';
import dynamic from 'next/dynamic';

import { Wrap } from '@leafwell/components';
import TrustSignals from './trust-signals';

const Accordion = dynamic(() => import('./accordion'));
const CardsBlock = dynamic(() => import('./cards-block'));
const CardsGrid = dynamic(() => import('./cards-grid'));
const RichContentHighlight = dynamic(() => import('./wysiwyg-content'));
const MedicalCardHighlight = dynamic(() => import('./medical-card-highlight'), {
  ssr: false,
});
const ReviewsBlock = dynamic(() => import('./reviews-block'));
const SeenOn = dynamic(() => import('./seen-on'));
const StatsHighlight = dynamic(() => import('./stats-highlight'));
const Team = dynamic(() => import('./team'));
const Info = dynamic(() => import('./info'));
const Method = dynamic(() => import('./method'));
const RoundedSection = dynamic(() => import('./rounded-section'));
const SignupFormBlock = dynamic(() => import('./signup-form-block'), {
  ssr: false,
});
const Faqs = dynamic(() => import('./faqs'));
const CardsList = dynamic(() => import('./cards-list'));
const ColumnList = dynamic(() => import('./column-list'));
const Carousel = dynamic(() => import('./carousel'));
const Intro = dynamic(() => import('./intro'));
const Line = dynamic(() => import('./line'));
const ConditionsRelated = dynamic(() => import('./conditions-related'));
const Marquee = dynamic(() => import('./marquee'));
const VideoYoutube = dynamic(() => import('./video-youtube'));
const Image = dynamic(() => import('./image'));
const MosaicCards = dynamic(() => import('./mosaic-cards'));
const TableBlock = dynamic(() => import('./table-block'));
const InfoIcons = dynamic(() => import('./info-icons'));
const SimpleCardCta = dynamic(() => import('./simple-card-cta'));
const TableOfContentsBlock = dynamic(() => import('./table-contents-block'));
const CardsImage = dynamic(() => import('./cards-image'));
const ContentAndMedia = dynamic(() => import('./content-and-media'));
const PriceComparison = dynamic(() => import('./price-comparison'));
const ContentAndBanner = dynamic(() => import('./content-and-banner'));
const PartnersLocation = dynamic(() => import('./partners-location'));
const FormBlock = dynamic(() => import('./form-block'), {
  ssr: false,
});
const Events = dynamic(() => import('./events-block'));
const OnceApproved = dynamic(() => import('./once-approved'));
const DateTime = dynamic(() => import('./date-time-block'));
const CityGuides = dynamic(() => import('./city-guides'));
const MobileScreenshots = dynamic(() => import('./mobile-screenshots'));
const TrustBrand = dynamic(() => import('./trust-brand'));
const BadgesList = dynamic(() => import('./badges-list'));
const BenefitsOfMMC = dynamic(() => import('./benefits-of-mmc'));
const CostSavingsCalculator = dynamic(
  () => import('./cost-savings-calculator'),
);
const TruncatedTextBox = dynamic(() => import('./truncated-text-box'));
const TitleImageAndContent = dynamic(() => import('./title-image-and-content'));
const FullWidthCta = dynamic(() => import('./full-width.cta'));
const ProductPlans = dynamic(() => import('./product-plans'));
const CardsWithHighlightAndImage = dynamic(
  () => import('./cards-with-highlight-and-image'),
);
const NumericalFacts = dynamic(() => import('./numerical-facts'));
const Testimonials = dynamic(() => import('./testimonials'));
const StepsCards = dynamic(() => import('./steps-cards'));
const CardsWithOptionalIcon = dynamic(
  () => import('./cards-with-optional-icon'),
);
const GridFourCells = dynamic(() => import('./grid-four-cells'));
const InfoWithIconAndBackground = dynamic(
  () => import('./info-with-icon-and-background'),
);
const MainNavigationItems = dynamic(() => import('./main-navigation-items'));
const SmallNote = dynamic(() => import('./small-note'));
const Clinic = dynamic(() => import('./clinic'));
const ClinicsLocation = dynamic(() => import('./clinics-location'));
const Persons = dynamic(() => import('./persons'));
const Ribbon = dynamic(() => import('./ribbon'));
const GoogleReviews = dynamic(() => import('./google-reviews'));
const Process = dynamic(() => import('./process'));
const MediaPanel = dynamic(() => import('./media-panel'));
const QualifyingConditions = dynamic(() => import('./qualifying-conditions'));
const QuickLinks = dynamic(() => import('./quick-links'));
const PriceCards = dynamic(() => import('./price-cards'));

type BlockProps = {
  blocks: BlocksType[];
  pageType?: string;
  modified?: string;
  hasReferral?: Page['acf']['hasReferral'];
  partnersReviews?: PartnersReviewsResult['partnersReviews'];
  stateProductImage?: Page['acf']['stateProductImage'];
  states?: Page['acf']['states'];
  showTabs?: boolean;
  variant?: 'control' | 'test' | null;
};

const Blocks: React.FC<BlockProps> = ({
  blocks,
  pageType,
  hasReferral,
  states,
  modified,
  stateProductImage,
  partnersReviews,
  showTabs = true,
}) => {
  return blocks ? (
    <>
      {blocks.map((block, key) => {
        switch (block.acfFcLayout) {
          case 'accordion':
            return <Accordion key={key} {...block} />;
          case 'cards':
            return <CardsBlock key={key} {...block} />;
          case 'medical_card_cta':
            return <MedicalCardHighlight key={key} {...block} />;
          case 'reviews':
            return (
              <ReviewsBlock
                key={key}
                pageType={pageType}
                reviews={partnersReviews}
                {...block}
              />
            );
          case 'seen_on':
            return <SeenOn key={key} {...block} />;
          case 'info':
            return <Info key={key} {...block} />;
          case 'line':
            return <Line key={key} {...block} />;
          case 'method':
            return <Method key={key} {...block} />;
          case 'rounded_section':
            return <RoundedSection key={key} {...block} />;
          case 'cards_list':
            return <CardsList key={key} {...block} />;
          case 'intro':
            return <Intro key={key} {...block} />;
          case 'stats_highlight':
            return <StatsHighlight key={key} {...block} />;
          case 'carousel':
            return <Carousel key={key} {...block} />;
          case 'sign_up_form':
            return (
              <SignupFormBlock
                key={key}
                title={block.title}
                subTitle={block.subTitle}
                formDescription={block.formDescription}
                address={block.address}
                sectionId={block.sectionId}
                layout={block.layout}
                formType={block.formType}
                formUrl={block.formUrl}
                pageType={pageType}
                hasReferral={hasReferral}
                activeStates={states}
                showTabs={showTabs}
                redirectMedicalCard={true}
                redirectCannabisCounseling={pageType === 'cannabis_counseling'}
              />
            );
          case 'faqs':
            return <Faqs key={key} pageType={pageType} {...block} />;
          case 'rich_text':
            return (
              <RichContentHighlight
                key={key}
                {...(block as WysiwygContentProps)}
              />
            );
          case 'medical_conditions':
            return <ConditionsRelated key={key} {...block} />;
          case 'blog_posts':
            return (
              <Wrap id={block.sectionId} size="large" key={key}>
                <CardsGrid
                  title={block.title}
                  items={block.posts}
                  displayBlogButton={true}
                  variant="smallFeatured"
                />
              </Wrap>
            );
          case 'marquee':
            return <Marquee key={key} {...block} />;
          case 'youtube_video':
            return <VideoYoutube key={key} {...block} />;
          case 'image':
            return <Image key={key} {...block} />;
          case 'mosaic_cards':
            return <MosaicCards key={key} {...block} />;
          case 'table':
            return (
              <TableBlock
                key={key}
                image={stateProductImage}
                state={states}
                {...block}
              />
            );
          case 'info_icons':
            return <InfoIcons key={key} {...block} />;
          case 'simple_card_cta':
            return <SimpleCardCta key={key} {...block} />;
          case 'table_of_contents':
            return <TableOfContentsBlock key={key} {...block} />;
          case 'cards_image':
            return <CardsImage key={key} {...block} />;
          case 'content_and_media':
            return <ContentAndMedia key={key} {...block} />;
          case 'price_comparison':
            return <PriceComparison key={key} modified={modified} {...block} />;
          case 'content_and_banner':
            return <ContentAndBanner key={key} {...block} />;
          case 'partners_location':
            return <PartnersLocation key={key} {...block} />;
          case 'form':
            return <FormBlock key={key} pageType={pageType} {...block} />;
          case 'events':
            return <Events key={key} {...block} />;
          case 'once_approved':
            return <OnceApproved key={key} {...block} />;
          case 'date_time':
            return <DateTime key={key} {...block} />;
          case 'team':
            return <Team key={key} {...block} />;
          case 'column_list':
            return <ColumnList key={key} {...block} />;
          case 'city_guides':
            return <CityGuides key={key} {...block} />;
          case 'image_and_description_list':
            return <MobileScreenshots key={key} {...block} />;
          case 'trust_brand':
            return <TrustBrand key={key} {...block} />;
          case 'badges_list':
            return <BadgesList key={key} {...block} />;
          case 'benefits_of_mmc':
            return <BenefitsOfMMC key={key} {...block} />;
          case 'employers_cost_savings_calculator':
            return <CostSavingsCalculator key={key} {...block} />;
          case 'truncated_text_box':
            return <TruncatedTextBox key={key} {...block} />;
          case 'title_image_and_content':
            return <TitleImageAndContent key={key} {...block} />;
          case 'full_width_cta':
            return <FullWidthCta key={key} {...block} />;
          case 'product_plans':
            return <ProductPlans key={key} {...block} />;
          case 'cards_with_highlight_option_and_image':
            return <CardsWithHighlightAndImage key={key} {...block} />;
          case 'numerical_facts':
            return <NumericalFacts key={key} {...block} />;
          case 'testimonials':
            return <Testimonials key={key} {...block} />;
          case 'steps_cards':
            return <StepsCards key={key} {...block} />;
          case 'cards_with_optional_icon':
            return <CardsWithOptionalIcon key={key} {...block} />;
          case 'grid_four_cells':
            return <GridFourCells key={key} {...block} />;
          case 'info_with_icon_and_background':
            return <InfoWithIconAndBackground key={key} {...block} />;
          case 'main_navigation_items':
            return <MainNavigationItems key={key} {...block} />;
          case 'small_note':
            return <SmallNote key={key} {...block} />;
          case 'clinic':
            return <Clinic key={key} {...block} />;
          case 'clinics_location':
            return <ClinicsLocation key={key} {...block} />;
          case 'persons':
            return <Persons key={key} {...block} />;
          case 'trust_signals':
            return <TrustSignals key={key} {...block} />;
          case 'ribbon':
            return <Ribbon key={key} {...block} />;
          case 'google_reviews':
            return <GoogleReviews key={key} {...block} />;
          case 'process':
            return <Process key={key} {...block} />;
          case 'media_panel':
            return <MediaPanel key={key} {...block} />;
          case 'qualifying_conditions':
            return <QualifyingConditions key={key} {...block} />;
          case 'quick_links':
            return <QuickLinks key={key} {...block} />;
          case 'price_cards':
            return <PriceCards key={key} {...block} />;
          default:
            return null;
        }
      })}
    </>
  ) : null;
};

export default Blocks;
